import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  IconButton,
  Tooltip,
  LinearProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem,
  Menu,
} from '@mui/material';
import {
  PlayArrow as ExecutionIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  FilterAlt as FilterAltIcon,
  AccountTree as WorkflowIcon,
} from '@mui/icons-material';
import { apiFetch } from '../../config/api';
import { formatDateTime } from '../../utils/dateUtils';
import { createIOSButtonStyles } from '../../theme/iosButtonStyles';
import { useTheme } from '@mui/material/styles';
import { SPACING } from '../../theme';
import { adjustOpacity } from '../../theme/utils';
import { debugError } from '../../utils/debugLog';
import {
  SummaryCards,
  ExecutionFilters,
  ExecutionViewDialog,
  WorkflowDetailsDialog,
} from './ExecutionListComponents';
import PaginationBar from '../common/PaginationBar';
import {
  StatusChip,
  ExperimentChip,
  SortIcon,
} from './ExecutionListComponents/utils';

const ExecutionList = () => {
  const theme = useTheme();
  const iosButtonStyles = createIOSButtonStyles(theme);

  const [executions, setExecutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  // Summary statistics from backend (for filtered results)
  const [summaryStats, setSummaryStats] = useState({
    successful_count: 0,
    failed_count: 0,
    experiment_count: 0,
  });

  // Sorting state
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc'); // Default: desc by created_at

  // Filter menu state for column headers
  const [typeFilterAnchor, setTypeFilterAnchor] = useState(null);
  const [statusFilterAnchor, setStatusFilterAnchor] = useState(null);

  // Filter states
  const [filters, setFilters] = useState({
    is_experiment: null, // null = all, true = experiments only, false = non-experiments only
    success: null, // null = all, true = successful only, false = failed only
    template_id: '',
    search: '',
  });

  // Debounced filters for template/template_id/search to reduce request spam
  const [debouncedFilters, setDebouncedFilters] = useState(filters);
  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedFilters(prev => ({
        ...prev,
        is_experiment: filters.is_experiment,
        success: filters.success,
        template_id: filters.template_id,
        search: filters.search,
      }));
    }, 500);
    return () => clearTimeout(handle);
  }, [
    filters.is_experiment,
    filters.success,
    filters.template_id,
    filters.search,
  ]);

  const [selectedExecution, setSelectedExecution] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);

  // Map frontend column names to backend database column names
  const mapColumnToBackend = useCallback(column => {
    const columnMap = {
      id: 'id',
      template_id: 'template_id',
      type: 'is_experiment', // Frontend "type" maps to backend "is_experiment"
      status: 'success', // Frontend "status" maps to backend "success"
      execution_time: 'execution_time',
      cost: 'cost',
      model_used: 'model_used',
      created_at: 'created_at',
      created_by: 'created_by',
    };
    return columnMap[column] || 'created_at';
  }, []);

  // Effective filters: only apply template_id/search when length >= 3
  // This ensures typing 1-2 characters doesn't trigger API calls
  const effectiveFilters = useMemo(() => {
    const templateIdValue =
      debouncedFilters.template_id && debouncedFilters.template_id.length >= 3
        ? debouncedFilters.template_id
        : '';
    const searchValue =
      debouncedFilters.search && debouncedFilters.search.length >= 3
        ? debouncedFilters.search
        : '';
    return {
      is_experiment: debouncedFilters.is_experiment,
      success: debouncedFilters.success,
      template_id: templateIdValue,
      search: searchValue,
    };
  }, [
    debouncedFilters.is_experiment,
    debouncedFilters.success,
    debouncedFilters.template_id,
    debouncedFilters.search,
  ]);

  const fetchExecutions = useCallback(async () => {
    setLoading(true);
    setError(null);

    // Build query params outside try block so it's accessible in catch block for error logging
    const queryParams = new URLSearchParams();
    queryParams.append('page', (page + 1).toString()); // API uses 1-based pagination
    queryParams.append('page_size', rowsPerPage.toString());

    // Apply filters - all filters are sent to backend for server-side filtering
    // Backend will query database with these conditions and return filtered results
    if (effectiveFilters.is_experiment !== null) {
      queryParams.append(
        'is_experiment',
        effectiveFilters.is_experiment.toString()
      );
    }
    if (effectiveFilters.success !== null) {
      queryParams.append('success', effectiveFilters.success.toString());
    }
    if (effectiveFilters.template_id) {
      queryParams.append('template_id', effectiveFilters.template_id);
    }
    if (effectiveFilters.search) {
      queryParams.append('search', effectiveFilters.search);
    }

    // Apply sorting - send sort_by and sort_order to backend
    // Map frontend column name to backend column name
    const backendSortBy = mapColumnToBackend(sortBy);
    queryParams.append('sort_by', backendSortBy);
    queryParams.append('sort_order', sortOrder);

    // Build URL for error logging
    const url = `/api/v1/templates/executions/?${queryParams.toString()}`;

    try {
      // API call with filters and sorting - backend will apply WHERE and ORDER BY in SQL query
      const data = await apiFetch(url);

      // Backend returns filtered and paginated results
      const executions = data.executions || [];
      setExecutions(executions);
      setTotalCount(data.total_count || 0); // Total count reflects filtered results

      // Update summary statistics from backend (for all filtered results, not just current page)
      // These stats reflect the filtered dataset, not just the current page
      if (
        data.successful_count !== undefined ||
        data.failed_count !== undefined ||
        data.experiment_count !== undefined
      ) {
        // Backend provides summary stats - use them
        setSummaryStats({
          successful_count: data.successful_count || 0,
          failed_count: data.failed_count || 0,
          experiment_count: data.experiment_count || 0,
        });
      } else {
        // Fallback: calculate from current page (for backward compatibility)
        // This is less accurate but better than showing 0
        setSummaryStats({
          successful_count: executions.filter(e => e.success).length,
          failed_count: executions.filter(e => !e.success).length,
          experiment_count: executions.filter(e => e.is_experiment).length,
        });
      }
    } catch (err) {
      // Show detailed error message to help debug issues
      let errorMessage = 'Failed to load executions.';

      if (err?.message) {
        errorMessage = err.message;
      } else if (err?.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err?.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (typeof err === 'string') {
        errorMessage = err;
      }

      setError(errorMessage);
      debugError('Error fetching executions:', {
        error: err,
        message: err?.message,
        stack: err?.stack,
        url: url,
        filters: effectiveFilters,
        sortBy,
        sortOrder,
        fullError: JSON.stringify(err, Object.getOwnPropertyNames(err)),
      });
    } finally {
      setLoading(false);
    }
  }, [
    page,
    rowsPerPage,
    effectiveFilters,
    sortBy,
    sortOrder,
    mapColumnToBackend,
  ]);

  useEffect(() => {
    fetchExecutions();
  }, [fetchExecutions]);

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value,
    }));
    // Reset to page 0 when filters change
    setPage(0);
  };

  // PaginationBar handlers (1-based pagination)
  const handlePrevPage = () => {
    if (page > 0) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    const totalPages = Math.ceil(totalCount / rowsPerPage) || 1;
    if (page < totalPages - 1) {
      setPage(page + 1);
    }
  };

  const handlePageClick = newPage => {
    // newPage is 1-based, convert to 0-based
    setPage(newPage - 1);
  };

  const handlePageSizeChange = newSize => {
    setRowsPerPage(newSize);
    setPage(0); // Reset to first page when page size changes
  };

  const handleViewExecution = execution => {
    setSelectedExecution(execution);
    setViewDialogOpen(true);
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setSelectedExecution(null);
  };

  // Handle column sorting - sends sort_by and sort_order to backend API
  // Backend applies SQL ORDER BY clause for server-side sorting
  const handleSort = column => {
    if (sortBy === column) {
      // Toggle sort order if clicking the same column
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to ascending
      setSortBy(column);
      setSortOrder('asc');
    }
    // Reset to page 0 when sorting changes
    setPage(0);
  };

  // Note: Sorting is now handled server-side via API parameters
  // We display executions as returned from the API
  const sortedExecutions = executions;

  // Handle filter menu open/close
  const handleTypeFilterOpen = event => {
    event.stopPropagation();
    setTypeFilterAnchor(event.currentTarget);
  };

  const handleTypeFilterClose = () => {
    setTypeFilterAnchor(null);
  };

  const handleStatusFilterOpen = event => {
    event.stopPropagation();
    setStatusFilterAnchor(event.currentTarget);
  };

  const handleStatusFilterClose = () => {
    setStatusFilterAnchor(null);
  };

  // Handle filter selection from header menu
  const handleTypeFilterSelect = value => {
    handleFilterChange('is_experiment', value === '' ? null : value);
    handleTypeFilterClose();
  };

  const handleStatusFilterSelect = value => {
    handleFilterChange('success', value === '' ? null : value);
    handleStatusFilterClose();
  };

  // Check if filter is active
  const isTypeFilterActive = filters.is_experiment !== null;
  const isStatusFilterActive = filters.success !== null;

  // Helper function to render sort icon
  const renderSortIcon = column => {
    return <SortIcon column={column} sortBy={sortBy} sortOrder={sortOrder} />;
  };

  // Helper function to get experiment chip
  const getExperimentChip = (isExperiment, experimentId, treatmentName) => {
    return (
      <ExperimentChip
        isExperiment={isExperiment}
        treatmentName={treatmentName}
      />
    );
  };

  // Helper function to get status chip
  const getStatusChip = success => {
    return <StatusChip success={success} />;
  };

  // Workflow dialog state and handlers
  const [workflowDialogOpen, setWorkflowDialogOpen] = useState(false);
  const [selectedWorkflowExecution, setSelectedWorkflowExecution] =
    useState(null);

  const handleViewWorkflow = execution => {
    setSelectedWorkflowExecution(execution);
    setWorkflowDialogOpen(true);
  };

  const handleCloseWorkflowDialog = () => {
    setWorkflowDialogOpen(false);
    setSelectedWorkflowExecution(null);
  };

  return (
    <Box sx={{ width: '100%', py: 4 }} data-testid="executions-page">
      <Container maxWidth="xl">
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={4}
        >
          <Typography
            variant="h4"
            component="h1"
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <ExecutionIcon fontSize="large" /> Execution List
          </Typography>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchExecutions}
            sx={{
              ...iosButtonStyles.secondary,
              textTransform: 'none',
              fontFamily:
                "'Inter', -apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif",
            }}
          >
            Refresh
          </Button>
        </Box>
      </Container>

      <SummaryCards totalCount={totalCount} summaryStats={summaryStats} />

      <Container maxWidth="xl">
        <ExecutionFilters
          filters={filters}
          onFilterChange={handleFilterChange}
        />

        {/* Executions Table - Modern Design */}
        <Paper
          sx={{
            borderRadius: 2,
            overflow: 'hidden',
            boxShadow: `0 1px 3px ${adjustOpacity(theme.palette.text.primary, 0.1)}`,
          }}
        >
          <Box
            sx={{ p: 3, borderBottom: `1px solid ${theme.palette.divider}` }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Executions ({totalCount} total)
            </Typography>
          </Box>
          {error && (
            <Alert
              severity="error"
              sx={{ m: 2 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => {
                    setError(null);
                    fetchExecutions();
                  }}
                >
                  Retry
                </Button>
              }
            >
              {error}
            </Alert>
          )}
          {loading && <LinearProgress sx={{ mb: 2 }} />}

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor:
                      theme.palette.mode === 'dark'
                        ? adjustOpacity(theme.palette.background.paper, 0.5)
                        : adjustOpacity(theme.palette.primary.main, 0.05),
                    '& .MuiTableCell-head': {
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      color: theme.palette.text.primary,
                      borderBottom: `2px solid ${theme.palette.divider}`,
                    },
                  }}
                >
                  <TableCell
                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('id')}
                  >
                    Execution ID {renderSortIcon('id')}
                  </TableCell>
                  <TableCell
                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('template_id')}
                  >
                    Template {renderSortIcon('template_id')}
                  </TableCell>
                  <TableCell
                    sx={{
                      cursor: 'pointer',
                      userSelect: 'none',
                      position: 'relative',
                    }}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      onClick={() => handleSort('type')}
                    >
                      <Box display="flex" alignItems="center">
                        Type {renderSortIcon('type')}
                      </Box>
                      <IconButton
                        size="small"
                        onClick={e => {
                          e.stopPropagation();
                          handleTypeFilterOpen(e);
                        }}
                        sx={{
                          ml: 1,
                          p: 0.5,
                          color: isTypeFilterActive
                            ? theme.palette.primary.main
                            : 'inherit',
                        }}
                      >
                        <FilterAltIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Menu
                      anchorEl={typeFilterAnchor}
                      open={Boolean(typeFilterAnchor)}
                      onClose={handleTypeFilterClose}
                      onClick={e => e.stopPropagation()}
                      PaperProps={{
                        sx: {
                          minWidth: 220,
                          '& .MuiMenuItem-root': {
                            minWidth: 220,
                            width: '100%',
                            padding: '8px 16px',
                            whiteSpace: 'nowrap',
                          },
                        },
                      }}
                    >
                      <MenuItem
                        onClick={() => handleTypeFilterSelect('')}
                        selected={filters.is_experiment === null}
                        sx={{ width: '100%' }}
                      >
                        All Executions
                      </MenuItem>
                      <MenuItem
                        onClick={() => handleTypeFilterSelect(false)}
                        selected={filters.is_experiment === false}
                        sx={{ width: '100%' }}
                      >
                        Regular Executions
                      </MenuItem>
                      <MenuItem
                        onClick={() => handleTypeFilterSelect(true)}
                        selected={filters.is_experiment === true}
                        sx={{ width: '100%' }}
                      >
                        Experiment Executions
                      </MenuItem>
                    </Menu>
                  </TableCell>
                  <TableCell
                    sx={{
                      cursor: 'pointer',
                      userSelect: 'none',
                      position: 'relative',
                    }}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      onClick={() => handleSort('status')}
                    >
                      <Box display="flex" alignItems="center">
                        Status {renderSortIcon('status')}
                      </Box>
                      <IconButton
                        size="small"
                        onClick={e => {
                          e.stopPropagation();
                          handleStatusFilterOpen(e);
                        }}
                        sx={{
                          ml: 1,
                          p: 0.5,
                          color: isStatusFilterActive
                            ? theme.palette.primary.main
                            : 'inherit',
                        }}
                      >
                        <FilterAltIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Menu
                      anchorEl={statusFilterAnchor}
                      open={Boolean(statusFilterAnchor)}
                      onClose={handleStatusFilterClose}
                      onClick={e => e.stopPropagation()}
                    >
                      <MenuItem
                        onClick={() => handleStatusFilterSelect('')}
                        selected={filters.success === null}
                      >
                        All Status
                      </MenuItem>
                      <MenuItem
                        onClick={() => handleStatusFilterSelect(true)}
                        selected={filters.success === true}
                      >
                        Successful
                      </MenuItem>
                      <MenuItem
                        onClick={() => handleStatusFilterSelect(false)}
                        selected={filters.success === false}
                      >
                        Failed
                      </MenuItem>
                    </Menu>
                  </TableCell>
                  <TableCell
                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('execution_time')}
                  >
                    Time (s) {renderSortIcon('execution_time')}
                  </TableCell>
                  <TableCell
                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('cost')}
                  >
                    Cost {renderSortIcon('cost')}
                  </TableCell>
                  <TableCell
                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('model_used')}
                  >
                    Model {renderSortIcon('model_used')}
                  </TableCell>
                  <TableCell
                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('created_at')}
                  >
                    Created {renderSortIcon('created_at')}
                  </TableCell>
                  <TableCell
                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('created_by')}
                  >
                    User {renderSortIcon('created_by')}
                  </TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedExecutions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No executions found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedExecutions.map(execution => (
                    <TableRow
                      key={execution.id}
                      hover
                      sx={{
                        '&:hover': {
                          backgroundColor:
                            theme.palette.mode === 'dark'
                              ? adjustOpacity(
                                  theme.palette.background.paper,
                                  0.5
                                )
                              : adjustOpacity(theme.palette.primary.main, 0.05),
                        },
                        '& .MuiTableCell-root': {
                          borderBottom: `1px solid ${theme.palette.divider}`,
                          padding: SPACING.md,
                        },
                      }}
                    >
                      <TableCell>
                        <Tooltip title={execution.id} placement="top">
                          <Typography variant="body2" fontFamily="monospace">
                            {execution.id.substring(0, 8)}...
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            gap: 0.25,
                            minWidth: 0, // Allow flexbox to shrink
                            width: '100%',
                          }}
                        >
                          {(() => {
                            const templateId = execution.template_id || '';
                            const templateName = execution.template_name;

                            // Check if we have a real template name (not None, not empty, and different from ID)
                            const hasTemplateName =
                              templateName &&
                              templateName.trim() !== '' &&
                              templateName !== templateId;

                            return (
                              <>
                                <Tooltip
                                  title={
                                    hasTemplateName
                                      ? `${templateName} (${templateId})`
                                      : templateId
                                  }
                                  placement="top"
                                  arrow
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: hasTemplateName ? 600 : 500,
                                      maxWidth: '100%',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      color: hasTemplateName
                                        ? 'text.primary'
                                        : 'text.secondary',
                                    }}
                                  >
                                    {hasTemplateName
                                      ? templateName
                                      : templateId}
                                  </Typography>
                                </Tooltip>
                                {/* Only show template ID below if we have a distinct name */}
                                {hasTemplateName && (
                                  <Tooltip
                                    title={templateId}
                                    placement="bottom"
                                    arrow
                                  >
                                    <Typography
                                      variant="caption"
                                      fontFamily="monospace"
                                      color="text.secondary"
                                      sx={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        maxWidth: '100%',
                                        fontSize: '0.7rem',
                                      }}
                                    >
                                      {templateId}
                                    </Typography>
                                  </Tooltip>
                                )}
                              </>
                            );
                          })()}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {getExperimentChip(
                          execution.is_experiment,
                          execution.experiment_id,
                          execution.treatment_name
                        )}
                      </TableCell>
                      <TableCell>{getStatusChip(execution.success)}</TableCell>
                      <TableCell>
                        {execution.execution_time?.toFixed(2)}
                      </TableCell>
                      <TableCell>
                        ${execution.cost?.toFixed(4) || '0.0000'}
                      </TableCell>
                      <TableCell>{execution.model_used}</TableCell>
                      <TableCell>
                        {formatDateTime(execution.created_at)}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {execution.created_by || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => handleViewExecution(execution)}
                              sx={{
                                color: theme.palette.primary.main,
                                '&:hover': {
                                  backgroundColor: adjustOpacity(
                                    theme.palette.primary.main,
                                    0.1
                                  ),
                                },
                              }}
                            >
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          {execution.has_workflow && (
                            <Tooltip title="View Workflow">
                              <IconButton
                                size="small"
                                onClick={() => handleViewWorkflow(execution)}
                                sx={{
                                  color: theme.palette.secondary.main,
                                  '&:hover': {
                                    backgroundColor: adjustOpacity(
                                      theme.palette.secondary.main,
                                      0.1
                                    ),
                                  },
                                }}
                              >
                                <WorkflowIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ p: 2 }}>
            <PaginationBar
              currentPage={page + 1} // Convert 0-based to 1-based
              totalPages={Math.ceil(totalCount / rowsPerPage) || 1}
              totalCount={totalCount}
              pageSize={rowsPerPage}
              onPrev={handlePrevPage}
              onNext={handleNextPage}
              onPageClick={handlePageClick}
              onPageSizeChange={handlePageSizeChange}
            />
          </Box>
        </Paper>

        {/* Execution Details Dialog */}
        <ExecutionViewDialog
          open={viewDialogOpen}
          onClose={handleCloseViewDialog}
          execution={selectedExecution}
        />

        {/* Workflow Details Dialog */}
        <WorkflowDetailsDialog
          open={workflowDialogOpen}
          onClose={handleCloseWorkflowDialog}
          executionId={selectedWorkflowExecution?.id}
          execution={selectedWorkflowExecution}
        />
      </Container>
    </Box>
  );
};

export default ExecutionList;
